// src/isPayload.ts
import { AsObjectFactory, isObject } from "@xylabs/object";
var isAnyPayload = (value) => {
  if (isObject(value)) {
    return typeof value.schema === "string";
  }
  return false;
};
var asAnyPayload = AsObjectFactory.create(isAnyPayload);
var isPayload = (schema) => (value) => {
  if (isAnyPayload(value)) {
    return schema.includes(value.schema);
  }
  return false;
};
var asPayload = (schema) => AsObjectFactory.create((value) => isPayload(schema)(value));

// src/isPayloadOfSchemaType.ts
function isPayloadOfSchemaType(schema) {
  return (x) => isAnyPayload(x) && x?.schema === schema;
}
var isPayloadOfSchemaTypeWithSources = (schema) => {
  return (x) => isPayloadOfSchemaType(schema)(x) && x.$sources != void 0 && Array.isArray(x.$sources);
};
var notPayloadOfSchemaType = (schema) => {
  return (x) => !isAnyPayload(x) || x?.schema !== schema;
};

// src/Error.ts
var ModuleErrorSchema = "network.xyo.error.module";
var isModuleError = isPayloadOfSchemaType(ModuleErrorSchema);

// src/PayloadBundle.ts
import { AsObjectFactory as AsObjectFactory2 } from "@xylabs/object";
var PayloadBundleSchema = "network.xyo.payload.bundle";
var isPayloadBundle = isPayloadOfSchemaType(PayloadBundleSchema);
var asPayloadBundle = AsObjectFactory2.create(isPayloadBundle);
var asOptionalPayloadBundle = AsObjectFactory2.createOptional(isPayloadBundle);

// src/PayloadSet/PayloadSetSchema.ts
var PayloadSetSchema = "network.xyo.payload.set";

// src/PayloadZod.ts
import { HashToJsonZod } from "@xylabs/hex";
import * as z3 from "zod";

// src/Schema.ts
import { AsTypeFactory } from "@xylabs/object";
import * as z from "zod";
var SchemaRegEx = /^(?:[a-z0-9]+\.)*[a-z0-9]+$/;
var SchemaZodV1 = z.string().regex(SchemaRegEx);
var SchemaZodV2 = z.string().regex(SchemaRegEx).transform((x) => x);
var SchemaZod = z.union([SchemaZodV1, SchemaZodV2]);
var makeSchema = (value) => {
  return z.templateLiteral([z.literal(value)]).transform((x) => x);
};
var PayloadSchema = "network.xyo.payload";
var PayloadSchemaZod = z.literal(PayloadSchema);
var isSchema = (value) => {
  return SchemaZod.safeParse(value).error === void 0;
};
var asSchema = AsTypeFactory.create(isSchema);

// src/StorageMeta/DataHash.ts
import { isHash } from "@xylabs/hex";
import { AsObjectFactory as AsObjectFactory3 } from "@xylabs/object";
var isDataHashStorageMeta = (value) => {
  return isHash(value?._dataHash);
};
var asDataHashStorageMeta = AsObjectFactory3.create(isDataHashStorageMeta);
var asOptionalDataHashStorageMeta = AsObjectFactory3.createOptional(isDataHashStorageMeta);

// src/StorageMeta/Hash.ts
import { isHash as isHash2 } from "@xylabs/hex";
import { AsObjectFactory as AsObjectFactory4 } from "@xylabs/object";
var isHashStorageMeta = (value) => {
  return isDataHashStorageMeta(value) && isHash2(value?._hash);
};
var asHashStorageMeta = AsObjectFactory4.create(isHashStorageMeta);
var asOptionalHashStorageMeta = AsObjectFactory4.createOptional(isHashStorageMeta);

// src/StorageMeta/Sequence.ts
import { AsObjectFactory as AsObjectFactory5 } from "@xylabs/object";
var isSequenceStorageMeta = (value) => {
  return value?._sequence != void 0;
};
var asSequenceStorageMeta = AsObjectFactory5.create(isSequenceStorageMeta);
var asOptionalSequenceStorageMeta = AsObjectFactory5.createOptional(isSequenceStorageMeta);

// src/StorageMeta/sequence/Parser.ts
import { toUint8Array } from "@xylabs/arraybuffer";
import { assertEx } from "@xylabs/assert";
import {
  isAddress,
  toAddress,
  toHex
} from "@xylabs/hex";

// src/StorageMeta/sequence/Sequence.ts
import { isHex } from "@xylabs/hex";
var isEpoch = (value) => {
  return isHex(value) && value.length === SequenceConstants.epochBytes * 2;
};
var isNonce = (value) => {
  return isHex(value) && value.length === SequenceConstants.nonceBytes * 2;
};
var isLocalSequence = (value) => {
  return isHex(value) && value.length === SequenceConstants.localSequenceBytes * 2;
};
var isQualifiedSequence = (value) => {
  return isHex(value) && value.length === SequenceConstants.qualifiedSequenceBytes * 2;
};
var isSequence = (value) => {
  return isLocalSequence(value) || isQualifiedSequence(value);
};
var SequenceNonceComponentLengths = {
  nonceIndexBytes: 4,
  nonceHashBytes: 4
};
var SequenceComponentLengths = {
  ...SequenceNonceComponentLengths,
  epochBytes: 8,
  nonceBytes: SequenceNonceComponentLengths.nonceIndexBytes + SequenceNonceComponentLengths.nonceHashBytes,
  addressBytes: 20
};
var SequenceComponentMinMax = {
  minEpoch: "0".repeat(SequenceComponentLengths.epochBytes * 2),
  maxEpoch: "f".repeat(SequenceComponentLengths.epochBytes * 2),
  minNonce: "0".repeat(SequenceComponentLengths.nonceBytes * 2),
  maxNonce: "f".repeat(SequenceComponentLengths.nonceBytes * 2),
  minAddress: "0".repeat(SequenceComponentLengths.addressBytes * 2),
  maxAddress: "f".repeat(SequenceComponentLengths.addressBytes * 2)
};
var LocalSequenceConstants = {
  ...SequenceComponentLengths,
  ...SequenceComponentMinMax,
  localSequenceBytes: SequenceComponentLengths.epochBytes + SequenceComponentLengths.nonceBytes,
  minLocalSequence: SequenceComponentMinMax.minEpoch + SequenceComponentMinMax.minNonce,
  maxLocalSequence: SequenceComponentMinMax.maxEpoch + SequenceComponentMinMax.maxNonce
};
var QualifiedSequenceConstants = {
  qualifiedSequenceBytes: LocalSequenceConstants.localSequenceBytes + SequenceComponentLengths.addressBytes,
  minQualifiedSequence: LocalSequenceConstants.minLocalSequence + SequenceComponentMinMax.minAddress,
  maxQualifiedSequence: LocalSequenceConstants.maxLocalSequence + SequenceComponentMinMax.maxAddress
};
var SequenceConstants = {
  ...LocalSequenceConstants,
  ...QualifiedSequenceConstants
};

// src/StorageMeta/sequence/Parser.ts
var SequenceParser = class _SequenceParser {
  static privateConstructorKey = Date.now().toString();
  data;
  constructor(privateConstructorKey, hex) {
    assertEx(_SequenceParser.privateConstructorKey === privateConstructorKey, () => "Use create function instead of constructor");
    const paddedHex = toHex(hex, {
      prefix: false,
      bitLength: hex.length <= SequenceConstants.localSequenceBytes * 2 ? SequenceConstants.localSequenceBytes * 8 : SequenceConstants.qualifiedSequenceBytes * 8
    });
    this.data = toUint8Array(paddedHex);
  }
  get address() {
    const start = SequenceConstants.localSequenceBytes;
    const end = SequenceConstants.qualifiedSequenceBytes;
    return toAddress(this.data.slice(start, end).buffer, { prefix: false });
  }
  get epoch() {
    const start = 0;
    const end = SequenceConstants.epochBytes;
    return toHex(this.data.slice(start, end).buffer, { prefix: false });
  }
  get localSequence() {
    const start = 0;
    const end = SequenceConstants.localSequenceBytes;
    return toHex(this.data.slice(start, end).buffer, { prefix: false });
  }
  get nonce() {
    const start = SequenceConstants.epochBytes;
    const end = SequenceConstants.localSequenceBytes;
    return toHex(this.data.slice(start, end).buffer, { prefix: false });
  }
  get qualifiedSequence() {
    const start = 0;
    const end = SequenceConstants.qualifiedSequenceBytes;
    return toHex(this.data.slice(start, end).buffer, { prefix: false });
  }
  static from(timestampOrSequence, nonceOrAddress, addressOrIndex, addressOnly) {
    const address = typeof addressOrIndex === "number" ? addressOnly : addressOrIndex;
    const index = typeof addressOrIndex === "number" ? addressOrIndex : void 0;
    if (isSequence(timestampOrSequence)) {
      if (nonceOrAddress) {
        assertEx(!isQualifiedSequence(timestampOrSequence), () => "Providing both a qualified sequence and a address is not allowed");
        assertEx(isAddress(nonceOrAddress), () => "Invalid address provided");
        return new this(_SequenceParser.privateConstructorKey, timestampOrSequence + address);
      }
      return new this(_SequenceParser.privateConstructorKey, timestampOrSequence);
    }
    const epoch = _SequenceParser.toEpoch(timestampOrSequence);
    const nonce = nonceOrAddress === void 0 ? void 0 : _SequenceParser.toNonce(nonceOrAddress, index);
    const addressHex = address ? toHex(address, { bitLength: SequenceConstants.addressBytes * 8 }) : SequenceConstants.minAddress;
    const hexString = epoch + nonce + addressHex;
    assertEx(isSequence(hexString), () => `Invalid sequence [${hexString}] [${epoch}, ${nonce}, ${addressHex}]`);
    return new this(_SequenceParser.privateConstructorKey, hexString);
  }
  static parse(value) {
    const hex = toHex(value);
    if (isSequence(hex)) {
      return new this(_SequenceParser.privateConstructorKey, hex);
    }
    throw new Error(`Invalid sequence [${value}]`);
  }
  // can convert a short number/hex to an epoch (treats it as the whole value) or extract an epoch from a sequence
  static toEpoch(value) {
    assertEx(
      typeof value !== "number" || Number.isInteger(value),
      () => "Value must be in integer"
    );
    const hex = toHex(value, { prefix: false });
    if (hex.length <= SequenceConstants.epochBytes * 2) {
      return toHex(value, { prefix: false, bitLength: SequenceConstants.epochBytes * 8 });
    }
    if (isSequence(hex)) {
      return hex.slice(0, SequenceConstants.epochBytes * 2);
    }
    throw new Error(`Value could not be converted to epoch [${hex}]`);
  }
  // can convert a short number/hex to a nonce (treats it as the whole value) or extract an nonce from a sequence
  static toNonce(value, index = 0) {
    assertEx(
      typeof value !== "number" || Number.isInteger(value),
      () => "Value must be in integer"
    );
    const hex = toHex(value, { prefix: false });
    if (isSequence(hex)) {
      return hex.slice(SequenceConstants.epochBytes * 2, SequenceConstants.localSequenceBytes * 2);
    }
    const hashHex = toHex(hex, { prefix: false, bitLength: SequenceConstants.nonceHashBytes * 8 }).slice(-SequenceConstants.nonceHashBytes * 2);
    const indexHex = toHex(index, { prefix: false, bitLength: SequenceConstants.nonceIndexBytes * 8 }).slice(-SequenceConstants.nonceIndexBytes * 2);
    return (indexHex + hashHex).slice(-SequenceConstants.nonceBytes * 2);
  }
};

// src/StorageMeta/sequence/Comparer.ts
var local = (a, b) => {
  const aa = SequenceParser.from(a);
  const bb = SequenceParser.from(b);
  return aa.localSequence > bb.localSequence ? 1 : aa.localSequence < bb.localSequence ? -1 : 0;
};
var qualified = (a, b) => {
  const aa = SequenceParser.from(a);
  const bb = SequenceParser.from(b);
  return aa.qualifiedSequence > bb.qualifiedSequence ? 1 : aa.qualifiedSequence < bb.qualifiedSequence ? -1 : 0;
};
var SequenceComparer = { local, qualified };

// src/StorageMeta/sequence/SequenceZod.ts
import { HexRegExMinMax, toHex as toHex2 } from "@xylabs/hex";
import * as z2 from "zod";
var LocalSequenceRegex = new RegExp(HexRegExMinMax(SequenceConstants.localSequenceBytes, SequenceConstants.localSequenceBytes));
var LocalSequenceToStringZod = z2.string().regex(LocalSequenceRegex);
var LocalSequenceFromStringZod = z2.string().regex(LocalSequenceRegex).transform((v) => toHex2(v));
var QualifiedSequenceRegex = new RegExp(HexRegExMinMax(SequenceConstants.qualifiedSequenceBytes, SequenceConstants.qualifiedSequenceBytes));
var QualifiedSequenceToStringZod = z2.string().regex(QualifiedSequenceRegex);
var QualifiedSequenceFromStringZod = z2.string().regex(QualifiedSequenceRegex).transform((v) => toHex2(v));
var SequenceToStringZod = z2.union([LocalSequenceToStringZod, QualifiedSequenceToStringZod]);
var SequenceFromStringZod = z2.union([LocalSequenceFromStringZod, QualifiedSequenceFromStringZod]);

// src/StorageMeta/StorageMeta.ts
import { AsObjectFactory as AsObjectFactory6 } from "@xylabs/object";
var isStorageMeta = (value) => {
  return isSequenceStorageMeta(value) && isHashStorageMeta(value);
};
var asStorageMeta = AsObjectFactory6.create(isStorageMeta);
var asOptionalStorageMeta = AsObjectFactory6.createOptional(isStorageMeta);

// src/PayloadZod.ts
var StorageMetaZod = z3.object({
  _hash: HashToJsonZod,
  _dataHash: HashToJsonZod,
  _sequence: SequenceToStringZod
});
var PayloadZod = z3.object({ schema: SchemaZod });
var PayloadWithStorageMetaZod = PayloadZod.extend(StorageMetaZod.shape);
var AnyPayloadZod = PayloadZod.catchall(z3.json());
var AnyPayloadWithStorageMetaZod = AnyPayloadZod.extend(StorageMetaZod.shape);
function WithStorageMetaZod(valueZod) {
  return StorageMetaZod.extend(valueZod.shape);
}
export {
  AnyPayloadWithStorageMetaZod,
  AnyPayloadZod,
  LocalSequenceConstants,
  LocalSequenceFromStringZod,
  LocalSequenceToStringZod,
  ModuleErrorSchema,
  PayloadBundleSchema,
  PayloadSchema,
  PayloadSchemaZod,
  PayloadSetSchema,
  PayloadWithStorageMetaZod,
  PayloadZod,
  QualifiedSequenceConstants,
  QualifiedSequenceFromStringZod,
  QualifiedSequenceToStringZod,
  SchemaRegEx,
  SchemaZod,
  SchemaZodV1,
  SchemaZodV2,
  SequenceComparer,
  SequenceComponentLengths,
  SequenceComponentMinMax,
  SequenceConstants,
  SequenceFromStringZod,
  SequenceNonceComponentLengths,
  SequenceParser,
  SequenceToStringZod,
  StorageMetaZod,
  WithStorageMetaZod,
  asAnyPayload,
  asDataHashStorageMeta,
  asHashStorageMeta,
  asOptionalDataHashStorageMeta,
  asOptionalHashStorageMeta,
  asOptionalPayloadBundle,
  asOptionalSequenceStorageMeta,
  asOptionalStorageMeta,
  asPayload,
  asPayloadBundle,
  asSchema,
  asSequenceStorageMeta,
  asStorageMeta,
  isAnyPayload,
  isDataHashStorageMeta,
  isEpoch,
  isHashStorageMeta,
  isLocalSequence,
  isModuleError,
  isNonce,
  isPayload,
  isPayloadBundle,
  isPayloadOfSchemaType,
  isPayloadOfSchemaTypeWithSources,
  isQualifiedSequence,
  isSchema,
  isSequence,
  isSequenceStorageMeta,
  isStorageMeta,
  makeSchema,
  notPayloadOfSchemaType
};
//# sourceMappingURL=index.mjs.map